import React, { useEffect, useState } from "react";
import { FaStar, FaQuoteRight, FaChevronLeft, FaChevronRight } from "react-icons/fa6";
import { Container, SectionHeading, GradientButton } from "./ui";

// NOTE: Replace / extend with real client reviews before going live.
const testimonials = [
  {
    quote: "The team delivered our website exactly the way we envisioned. The communication was smooth, and they were always open to feedback. Highly recommended!",
    name: "Rahul Mehta",
    role: "Founder, Healthomax",
    tone: "bg-orange-100 text-orange-600",
    quoteTone: "text-blue-200",
  },
  {
    quote: "They built our e-commerce platform with great attention to detail. The solution has helped us grow our online sales significantly. Professional team and excellent support.",
    name: "Priya Sharma",
    role: "Co-founder, StyleCart",
    tone: "bg-fuchsia-100 text-fuchsia-600",
    quoteTone: "text-fuchsia-200",
  },
  {
    quote: "Working with this team was a great experience. They understood our business needs, provided valuable suggestions, and delivered a high-quality solution on time.",
    name: "Amit Verma",
    role: "CEO, TripNest",
    tone: "bg-emerald-100 text-emerald-600",
    quoteTone: "text-emerald-200",
  },
];

const usePerView = () => {
  const get = () => (window.innerWidth >= 1024 ? 3 : window.innerWidth >= 640 ? 2 : 1);
  const [perView, setPerView] = useState(get);
  useEffect(() => {
    const onResize = () => setPerView(get());
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);
  return perView;
};

const initials = (name) => name.split(" ").map((p) => p[0]).join("");

const Testimonials = () => {
  const perView = usePerView();
  const maxIndex = Math.max(0, testimonials.length - perView);
  const [index, setIndex] = useState(0);

  useEffect(() => {
    if (index > maxIndex) setIndex(maxIndex);
  }, [index, maxIndex]);

  useEffect(() => {
    if (maxIndex === 0) return undefined;
    const id = setInterval(() => setIndex((i) => (i >= maxIndex ? 0 : i + 1)), 5000);
    return () => clearInterval(id);
  }, [maxIndex]);

  const go = (dir) => setIndex((i) => Math.min(maxIndex, Math.max(0, i + dir)));
  const navBtn =
    "absolute top-1/2 z-10 inline-flex h-[44px] w-[44px] -translate-y-1/2 items-center justify-center rounded-full border-0 bg-white text-ink shadow-card transition hover:text-brand-blue disabled:opacity-40";

  return (
    <section className="bg-gradient-to-b from-white via-[#F7F8FF] to-white py-[40px] lg:py-[46px]">
      <Container>
        <SectionHeading
          badge="Client experiences"
          title="Good Work Builds"
          highlight="Lasting Relationships."
          subtitle="We believe successful projects come from listening closely, collaborating openly, and delivering work that meets real business needs."
        />

        <div className="relative mt-[48px] px-[8px] sm:px-[56px]">
          <button type="button" aria-label="Previous" onClick={() => go(-1)} disabled={index === 0} className={`${navBtn} left-0 hidden sm:inline-flex`}>
            <FaChevronLeft />
          </button>
          <div className="overflow-hidden">
            <div className="flex transition-transform duration-500" style={{ transform: `translateX(-${(100 / perView) * index}%)` }}>
              {testimonials.map((t) => (
                <div key={t.name} className="shrink-0 px-[10px]" style={{ width: `${100 / perView}%` }}>
                  <figure className="flex h-full flex-col rounded-3xl bg-white p-[28px] shadow-card ring-1 ring-slate-100">
                    <div className="flex items-start justify-between">
                      <div className="flex gap-1 text-[16px] text-amber-400">
                        {[0, 1, 2, 3, 4].map((i) => (
                          <FaStar key={i} />
                        ))}
                      </div>
                      <FaQuoteRight className={`text-[34px] ${t.quoteTone}`} />
                    </div>
                    <blockquote className="mt-[16px] flex-1 text-[16px] leading-relaxed text-slate-700">“{t.quote}”</blockquote>
                    <figcaption className="mt-6 flex items-center gap-[12px]">
                      <span className={`inline-flex h-[48px] w-[48px] items-center justify-center rounded-full text-[15px] font-bold ${t.tone}`}>
                        {initials(t.name)}
                      </span>
                      <div>
                        <p className="text-[16px] font-bold text-ink">{t.name}</p>
                        <p className="text-[13px] text-slate-500">{t.role}</p>
                      </div>
                    </figcaption>
                  </figure>
                </div>
              ))}
            </div>
          </div>
          <button type="button" aria-label="Next" onClick={() => go(1)} disabled={index === maxIndex} className={`${navBtn} right-0 hidden sm:inline-flex`}>
            <FaChevronRight />
          </button>
        </div>

        {maxIndex > 0 && (
          <div className="mt-6 flex justify-center gap-2">
            {Array.from({ length: maxIndex + 1 }).map((_, i) => (
              <button
                key={i}
                type="button"
                aria-label={`Go to slide ${i + 1}`}
                onClick={() => setIndex(i)}
                className={`h-2 rounded-full border-0 p-0 transition-all ${i === index ? "w-6 bg-brand-blue" : "w-2 bg-slate-300"}`}
              />
            ))}
          </div>
        )}

        <div className="mt-[40px] text-center">
          <GradientButton href="/our-completed-projects">Read More Client Stories</GradientButton>
        </div>
      </Container>
    </section>
  );
};

export default Testimonials;
